import type { AuthoredEdge, AuthoredGraph, AuthoredNode, CreateOutput, PaletteSource } from './types';

export const AUTHORED_GRAPH_KIND = 'authored_graph';

export function emptyGraph(appId: string): AuthoredGraph {
  return { app_id: appId, nodes: [], edges: [], updated_at: null };
}

export function parseAuthoredGraph(raw: unknown, appId: string): AuthoredGraph {
  const fallback = emptyGraph(appId);
  if (!raw || typeof raw !== 'object') return fallback;
  const doc = raw as Partial<AuthoredGraph> & { graph?: AuthoredGraph };
  const body = doc.graph && typeof doc.graph === 'object' ? doc.graph : doc;
  const nodes = Array.isArray(body.nodes) ? body.nodes.filter(isNode) : [];
  const edges = Array.isArray(body.edges) ? body.edges.filter(isEdge) : [];
  const storedApp = String(body.app_id || '').trim();
  if (storedApp && storedApp !== appId) return fallback;
  return {
    app_id: appId,
    nodes,
    edges,
    updated_at: typeof body.updated_at === 'string' ? body.updated_at : null,
  };
}

function isNode(value: unknown): value is AuthoredNode {
  if (!value || typeof value !== 'object') return false;
  const node = value as AuthoredNode;
  return Boolean(node.id && (node.kind === 'source' || node.kind === 'output') && (node.class === 'ingest' || node.class === 'iot'));
}

function isEdge(value: unknown): value is AuthoredEdge {
  if (!value || typeof value !== 'object') return false;
  const edge = value as AuthoredEdge;
  return Boolean(edge.id && edge.source && edge.target);
}

export function dropSource(
  graph: AuthoredGraph,
  source: PaletteSource,
  position: { x: number; y: number },
  _outputs?: CreateOutput[]
): AuthoredGraph {
  if (graph.nodes.some((node) => node.kind === 'source' && node.sourceId === source.id)) {
    return graph;
  }
  const sourceNode: AuthoredNode = {
    id: `node:${source.id}`,
    kind: 'source',
    class: source.class,
    sourceId: source.id,
    label: source.label,
    status: source.status,
    x: position.x,
    y: position.y,
  };
  return { ...graph, nodes: [...graph.nodes, sourceNode] };
}

export function connectSourceToDest(
  graph: AuthoredGraph,
  sourceNodeId: string,
  destId: string,
  destClass?: AuthoredNode['class']
): AuthoredGraph {
  const source = graph.nodes.find((node) => node.id === sourceNodeId && node.kind === 'source');
  if (!source || !destId) return graph;
  if (destClass && source.class !== destClass) return graph;
  const edgeId = `edge:${sourceNodeId}->${destId}`;
  if (graph.edges.some((edge) => edge.id === edgeId)) return graph;
  return {
    ...graph,
    edges: [...graph.edges, { id: edgeId, source: sourceNodeId, target: destId }],
  };
}

export function connectSourceToOutput(
  graph: AuthoredGraph,
  sourceNodeId: string,
  outputNodeId: string
): AuthoredGraph {
  const source = graph.nodes.find((node) => node.id === sourceNodeId && node.kind === 'source');
  const output = graph.nodes.find((node) => node.id === outputNodeId && node.kind === 'output');
  if (!source || !output) return graph;
  if (source.class !== output.class) return graph;
  return connectSourceToDest(graph, sourceNodeId, outputNodeId, output.class);
}

export function removeAuthoredNode(graph: AuthoredGraph, nodeId: string): AuthoredGraph {
  return {
    ...graph,
    nodes: graph.nodes.filter((node) => node.id !== nodeId),
    edges: graph.edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
  };
}

export function serializeAuthoredGraph(graph: AuthoredGraph): {
  app_id: string;
  kind: string;
  nodes: AuthoredNode[];
  edges: AuthoredEdge[];
} {
  return {
    app_id: graph.app_id,
    kind: AUTHORED_GRAPH_KIND,
    nodes: graph.nodes,
    edges: graph.edges,
  };
}
